import {
  DocumentRegisterForm, DocumentUpdateForm, getUpdateRequestBody,
} from '../services/http/documents.api';
import {
  ElectronicFlg, ElectronicFlgLabels, PaperFlg, PaperFlgLabels,
} from './document.utils';
import FileUtils from './file.utils';
import Formatter from './formatters';

export default class Validator {
  /**
   * パスワードに使用できる記号の文字列。
   */
  static passwordSymbols = '.-_&$#@!=';

  /**
   * パスワードに使用できる記号の数を返す。
   */
  static passwordSymbolsLength = () => Validator.passwordSymbols.length;

  /**
   * パスワードの最小文字数。
   */
  static passwordLengthMin = 8;

  /**
   * パスワードの最大文字数。
   */
  static passwordLengthMax = 20;

  /**
   * ユーザー名の最大文字数。
   */
  static userNameLengthMax = 20;

  /**
   * 緊急連絡先の最大文字数。
   */
  static emergencyContactsMax = 25;

  /**
   * 連絡先のメッセージの最大文字数。
   */
  static contactMessageMax = 100;

  /**
   * お客様メモの最大文字数。
   */
  static userMemoMax = 100;

  /**
   * ドキュメント名の最大文字数。
   */
  static documentNameLengthMax = 200;

  /**
   * PDFファイル名の最大文字数。
   */
  static documentFileNameLengthMax = 116 + 4; // (116 + 4 (.pdf))

  /**
   * 添付ファイル名の最大文字数。
   */
  static attachmentFileNameLengthMax = 120;

  /**
   * csvDateRegexは、CSVファイル内の日付書式のバリデーションに使用する正規表現。
   */
  static csvDateRegex = /^\d{4}\/\d{2}\/\d{2}$/;

  /**
   * ユーザー名が有効かどうかを確認する。
   *
   * @param name - ユーザー名
   */
  static validUserName(name: string) {
    if (!name || name.length > Validator.userNameLengthMax) throw new Error(`${Validator.userNameLengthMax}文字以内で入力してください`);
  }

  /**
   * パスワードが有効な文字であるかどうかを確認する。
   *
   * @param password - パスワードの文字列
   * @throws - パスワードが有効な文字でない場合にエラーを投げる
   */
  static validPasswordTypeChars(password: string) {
    if (!password.match(/^[a-zA-Z0-9.\-_&$#@!=]{8,20}$/)) throw new Error(`利用可能な文字は半角英数字及び${Validator.passwordSymbolsLength()}種類の記号(${Validator.passwordSymbols})となります`);
  }

  /**
   * パスワードが有効な文字であるかどうかを確認する。
   *
   * @param password - パスワードの文字列
   * @throws - 大文字英、小文字英、数字の3種類(いずれも半角)を必ず含めてください
   */
  static validPasswordChars(password: string) {
    if (!password.match(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/)) throw new Error('大文字英、小文字英、数字の3種類(いずれも半角)を必ず含めてください');
  }

  /**
   * パスワードの長さが有効かどうかを確認する。
   *
   * @param password - パスワードの文字列
   * @throws - パスワードの長さが有効でない場合にエラーを投げる
   */
  static validPasswordLength(password: string) {
    if (password.length < Validator.passwordLengthMin || password.length > Validator.passwordLengthMax) throw new Error(`パスワードの文字数が使用可能範囲外です。パスワードの文字数は半角${Validator.passwordLengthMin}文字以上${Validator.passwordLengthMax}文字以内で入力してください。`);
  }

  /**
   * 2つのパスワードが一致するかどうかを確認する。
   *
   * @param password - パスワードの文字列
   * @param other - 確認するパスワードの文字列
   * @throws パスワードが一致しません
   */
  static comparePassword(password: string, other: string) {
    if (password !== other) throw new Error('パスワードが一致しません');
  }

  /**
   * テナントコードが有効かどうかを確認する。
   *
   * @param tenant - テナントコードの文字列
   * @throws - テナントコード無効
   */
  static validateTenantCode(tenant: string) {
    if (tenant.length < 1) throw Error('テナントコード無効');
  }

  /**
   * メールアドレスが有効かどうかを確認する。
   *
   * @param email - メールアドレスの文字列
   * @throws メールが無効です
   */
  static validateEmail(email: string) {
    if (!email.match(/.+?@.+?\..+/)) throw new Error('メールが無効です');
  }

  /**
   * 文字列が有効な日付形式かどうかを確認する。
   *
   * @param val - CSVの文字列
   * @returns 日付形式であればtrue, そうでなければfalse
   */
  static csvStringIsValidDate(val?: string): boolean {
    if (val) return Formatter.fromFormatToDateTime(val, 'yyyy/MM/dd').isValid;
    return false;
  }

  /**
   * 有効な契約種別かどうかを確認する。
   *
   * @param electronicFlg - 契約種別
   * @returns - エラーメッセージ
   */
  static csvStringValidateElectronicFlg(electronicFlg?: number) {
    if (
      electronicFlg == null
      || !Object.keys(ElectronicFlgLabels).includes(String(electronicFlg))
    ) return '契約種別には「電子契約」、「書面契約」の\nどちらかを入力してください。';
    return '';
  }

  /**
   * 有効な書面有無かどうかを確認する。
   *
   * @param paperFlg - 書面有無
   * @param electronicFlg - 契約種別
   * @returns エラーメッセージ
   */
  static csvStringValidatePaperFlg(paperFlg?: number, electronicFlg?: number) {
    if (
      electronicFlg == null || paperFlg == null
      || !Object.keys(PaperFlgLabels).includes(String(paperFlg))
    ) return '書面有無には「あり」、「なし」の\nどちらかを入力してください。';
    if (
      electronicFlg === ElectronicFlg.ELECTRONIC
      && paperFlg === PaperFlg.WITH_PAPER
    ) return '「書面有無」が不正です。';
    return '';
  }

  /**
   * 文字列が空でないかどうかを確認する。
   *
   * @param val - CSVの文字列
   * @returns - 空でなければtrue、空であればfalse
   */
  static csvStringIsNotEmpty(val?: string): boolean {
    return Formatter.validString(val) !== null;
  }

  /**
   * リクエストボディが指定サイズを超えているか確認します。
   *

   * @returns trueならサイズオーバー
   */
  static async validateDocumentRegisterFormIsTooLarge(form: DocumentRegisterForm) {
    const requestBody = {
      documentName: form.documentName,
      documentFile: form.documentFile ? await FileUtils.toBase64(form.documentFile) : undefined,
      documentFileName: form.documentFileName,
      documentFileSize: form.documentFile?.size,
      documentClassification: form.documentClassification,
      documentTypeId: form.documentTypeId,
      documentTypeName: form.documentTypeName,
      paperFlg: form.paperFlg,
      electronicFlg: form.electronicFlg,
      barcodePrinting: form.barcodePrinting,
      folderId: form.folderId,
      memo: form.memo,
      tagList: form.tagList ? form.tagList.map((tag) => ({
        tagLabel: tag.tagLabel,
        format: tag.format,
        formatCheck: tag.formatCheck ?? 0,
        value: tag.value,
      })) : undefined,
      relationalDocumentList: form.relationalDocumentList ? form.relationalDocumentList.map((relationalDocument) => ({
        relationalDocumentId: relationalDocument.id,
      })) : undefined,
      attachmentFileList: form.attachmentFileList ? await Promise.all(
        form.attachmentFileList
          .filter((attachment) => attachment.attachmentFile)
          .map(async (attachment) => ({
            attachmentFile: attachment.attachmentFile ? await FileUtils.toBase64(attachment.attachmentFile) : undefined,
            attachmentFileName: attachment.attachmentFileName,
            attachmentFileSize: attachment.attachmentFile?.size,
          })),
      ) : undefined,
    };

    return FileUtils.isTooLarge(requestBody);
  }

  /**
   * リクエストボディが指定サイズを超えているか確認します。
   *

   * @returns trueならサイズオーバー
   */
  static async validateDocumentUpdateFormIsTooLarge(form: DocumentUpdateForm) {
    const payload = await getUpdateRequestBody(form);

    return FileUtils.isTooLarge(payload);
  }

  /**
   * ドキュメント名が有効かどうかを確認する。
   *
   * @param name - ドキュメント名
   * @returns 有効であればtrue、無効であればfalse
   */
  static isInvalidDocumentName(name: string): boolean {
    return name.length > Validator.documentNameLengthMax;
  }

  /**
   * PDFファイル名が有効かどうかを確認する。
   *

   * @returns エラーメッセージ
   */
  static validateDocumentFileName(fileName?: string) {
    const errorMessage = 'PDFファイル名が不正です。';
    if (!fileName || fileName.length > Validator.documentFileNameLengthMax) return `ファイル名が${Validator.documentFileNameLengthMax}文字を超えています。`;
    const regex = /\.pdf$/i; // ends with .pdf (case-insensitive)
    return regex.test(fileName) ? '' : errorMessage;
  }

  /**
   * 添付ファイル名が有効かどうかを確認する。
   *

   * @returns エラーメッセージ
   */
  static validateAttachmentFileName(fileName?: string) {
    if (!fileName || fileName.length > Validator.attachmentFileNameLengthMax) return `ファイル名が${Validator.attachmentFileNameLengthMax}文字を超えています。`;
    return '';
  }
}
